import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getListEmailCampaignsQueryKey, useListEmailCampaigns } from '@cdf/api-client-react';
import { AgentPanel } from '@/components/agent/agent-panel';
import { useAuth } from '@/components/auth/auth-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { promptContextForRoute } from '@/components/agent/prompt-library-context';
import { Button } from '@/components/ui/button';

export function CampaignAwareAgentPanel() {
  const location = useLocation();
  const id = new URLSearchParams(location.search).get('campaignId');
  if (location.pathname !== '/content-hub/mailing-list' || !id || !/^[0-9a-f-]{36}$/i.test(id)) return <AgentPanel />;
  return <ExactCampaignPanel key={id} id={id} />;
}
function ExactCampaignPanel({ id }: { id: string }) {
  const auth = useAuth(), access = useTenantEntity(), client = useQueryClient();
  const identity = JSON.stringify([auth.account?.stableId, access.selected?.tenantId, access.selected?.entityId,
    access.status, access.hasPermission('entity.content.manage'), access.hasPermission('entity.records.read')]);
  const params = { limit: 25, offset: 0, campaignId: id };
  const queryKey = [...getListEmailCampaignsQueryKey(params), identity];
  const ready = Boolean(auth.account?.stableId && access.status === 'ready' && access.selected);
  const result = useListEmailCampaigns(params, { query: { queryKey, enabled: ready, retry: false, staleTime: 0, gcTime: 0 } });
  React.useEffect(() => () => { void client.cancelQueries({ queryKey, exact: true }); }, [client, JSON.stringify(queryKey)]);
  const campaign = ready && !result.isError ? result.data?.items.find(row => row.id === id) : undefined;
  if (!campaign || !access.selected) return <Button variant="ghost" size="sm" disabled title="This campaign's access must be checked before opening its Agent.">Campaign Agent unavailable</Button>;
  return <div hidden={result.isFetching} inert={result.isFetching}><AgentPanel key={identity + ':' + id} record={{ context: { kind: 'campaign', ...access.selected, recordId: id },
    label: campaign.name, canWrite: access.hasPermission('entity.content.manage'), canAdminister: access.hasPermission('entity.manage'),
    prompts: promptContextForRoute('/content-hub/mailing-list') }} /></div>;
}
